/**
 * ValueSourceSelector
 *
 * Track S5 UI: lets a Cycle Owner or Track Owner pick which published
 * Value Collection will be operative for this track.
 *
 * Per correction-E §2 — when the track is locked, this component is
 * replaced by the locked provenance indicator (rendered by the parent).
 * When no collections exist, shows an actionable empty state.
 */

import { useEffect, useState, useCallback } from 'react';
import { ChevronDown, CheckCircle2, Database, Loader2, Info } from 'lucide-react';
import { useValueCollectionStore, type ValueCollection } from '@/stores/prioritization/valueCollectionStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import type { Track } from '@/types/prioritization/track';

interface ValueSourceSelectorProps {
  track: Track;
  workspaceId: string;
  actorId: string;
  /** Called when a collection is successfully selected so parent can re-fetch. */
  onSelected?: () => void;
}

export function ValueSourceSelector({
  track,
  workspaceId,
  actorId,
  onSelected,
}: ValueSourceSelectorProps) {
  const { collections, isLoading, fetchValueCollections, selectValueSource } =
    useValueCollectionStore();
  const { users } = useSystemUsers();

  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);

  useEffect(() => {
    fetchValueCollections(workspaceId);
  }, [workspaceId, fetchValueCollections]);

  const published = collections.filter(c => c.status === 'published');
  const selectedCollection: ValueCollection | undefined = collections.find(
    c => c.id === track.valueCollectionId
  );

  const resolveOwnerName = useCallback(
    (ownerId: string) => {
      const u = users.find(u => u.id === ownerId);
      return u?.name ?? u?.email ?? 'Unknown';
    },
    [users]
  );

  const handleSelect = useCallback(
    async (collectionId: string | null) => {
      if (busy) return;
      setOpen(false);
      setSelectError(null);
      // Choosing the selection already in force does nothing.
      if ((collectionId ?? null) === (track.valueCollectionId ?? null)) return;
      setBusy(true);
      try {
        const ok = await selectValueSource({
          trackId: track.id,
          valueCollectionId: collectionId,
          actorId,
          workspaceId,
        });
        if (ok) {
          onSelected?.();
        } else {
          setSelectError(collectionId
            ? 'Could not select value collection. Make sure it is published and the track is not locked.'
            : 'Could not clear the value collection. The track may already be locked.');
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, selectValueSource, track.id, track.valueCollectionId, actorId, workspaceId, onSelected]
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading value collections…
      </div>
    );
  }

  // Empty state: no published collections exist in workspace
  if (published.length === 0 && !selectedCollection) {
    return (
      <div className="flex items-start gap-3 border border-dashed border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
        <Database className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[var(--text-sec)]">
          <span>No value collections available — the workspace&apos;s official values will be used at lock time. </span>
          <span className="text-[var(--text-ter)] italic">
            Create and publish a collection in the Value Collections settings to use a curated value set.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Selection button */}
      <div className="relative">
        <button
          type="button"
          disabled={busy}
          onClick={() => !busy && setOpen(v => !v)}
          className={[
            'w-full flex items-center justify-between gap-2  border px-3 py-2 text-sm text-left transition-colors',
            'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--slate)] cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500',
            busy ? 'opacity-60 cursor-not-allowed' : '',
          ].join(' ')}
        >
          <span className="flex items-center gap-2 min-w-0">
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin flex-shrink-0 text-[var(--text-ter)]" />
            ) : selectedCollection ? (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-[var(--green)]" />
            ) : (
              <Database className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
            )}
            <span className="truncate">
              {selectedCollection
                ? `${selectedCollection.name} v${selectedCollection.version} · ${resolveOwnerName(selectedCollection.ownerId)}`
                : 'No collection selected — the workspace\'s official values will be used at lock time'}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute z-50 mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg overflow-hidden">
            <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--rule)]">
              {/* Use workspace values option */}
              <li>
                <button
                  type="button"
                  onClick={() => void handleSelect(null)}
                  className={[
                    'w-full px-3 py-2 text-sm text-left hover:bg-[var(--surface)] transition-colors',
                    !selectedCollection ? 'bg-[var(--surface)] text-[var(--text-sec)] italic' : 'text-[var(--text-ter)] italic',
                  ].join(' ')}
                >
                  — Use the workspace's official values at lock time
                </button>
              </li>
              {published.map(c => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c.id)}
                    className={[
                      'w-full px-3 py-2 text-sm text-left hover:bg-[var(--slate-dim)] transition-colors',
                      selectedCollection?.id === c.id
                        ? 'bg-[var(--slate-dim)] font-medium text-[var(--text-sec)]'
                        : 'text-[var(--text-pri)]',
                    ].join(' ')}
                  >
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-[var(--text-ter)] text-xs">v{c.version}</span>
                      <span className="text-[var(--text-ter)] text-xs">by {resolveOwnerName(c.ownerId)}</span>
                    </div>
                    {c.description && (
                      <p className="mt-0.5 text-xs text-[var(--text-ter)] truncate">{c.description}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Inheritance description */}
      {selectedCollection ? (
        <p className="flex items-start gap-1.5 text-xs text-[var(--text-ter)]">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
          Values from:{' '}
          <span className="font-medium text-[var(--text-sec)]">
            {selectedCollection.name} v{selectedCollection.version}
          </span>{' '}
          by {resolveOwnerName(selectedCollection.ownerId)} ·{' '}will be snapshotted at lock time
        </p>
      ) : (
        <p className="flex items-start gap-1.5 text-xs text-[var(--text-ter)]">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
          No collection selected — the workspace&apos;s official values will be used at lock time
        </p>
      )}

      {selectError && (
        <p className="text-xs text-[var(--coral)] mt-1">{selectError}</p>
      )}

      {/* Close dropdown on outside click */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Locked Provenance Indicator (rendered instead of selector after lock)
// ============================================================================

interface LockedProvenanceProps {
  track: Track;
  workspaceId: string;
}

export function ValueSourceLockedProvenance({ track, workspaceId }: LockedProvenanceProps) {
  const { collections, fetchValueCollections } = useValueCollectionStore();
  const { users } = useSystemUsers();

  useEffect(() => {
    fetchValueCollections(workspaceId);
  }, [workspaceId, fetchValueCollections]);

  const collection = collections.find(c => c.id === track.valueCollectionId);
  const lockedBy = track.valueSourceSelectedBy
    ? users.find(u => u.id === track.valueSourceSelectedBy)
    : null;
  const lockedByName = lockedBy?.name ?? lockedBy?.email ?? 'Unknown';
  const lockedAt = track.valuesConvergedAt;
  const snapshotId = track.valueCollectionSnapshotId;
  const isAutoLocked = !track.valueSourceSelectedAt && !track.valueCollectionId;

  return (
    <div className="border border-[var(--green)] bg-[var(--green-dim)] px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
        <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
          Track values locked {isAutoLocked ? 'automatically' : 'manually'}
          {lockedAt ? ` on ${new Date(lockedAt).toLocaleString()}` : ''}
          {!isAutoLocked && lockedByName !== 'Unknown' ? ` by ${lockedByName}` : ''}
        </span>
      </div>

      <div className="pl-6 space-y-0.5 text-xs text-[var(--text-sec)]">
        <div>
          <span className="text-[var(--text-ter)]">Value source: </span>
          {collection ? (
            <span className="font-medium text-[var(--text-pri)]">
              {collection.name} v{collection.version}
            </span>
          ) : (
            <span className="italic text-[var(--text-ter)]">The workspace&apos;s official values (no collection selected)</span>
          )}
        </div>
        {snapshotId && (
          <div>
            <span className="text-[var(--text-ter)]">Snapshot ID: </span>
            <span className="font-mono text-[var(--text-sec)]">{snapshotId.slice(0, 8)}…</span>
            <span className="ml-1 text-[var(--text-ter)] italic text-xs">(audit reference)</span>
          </div>
        )}
      </div>
    </div>
  );
}
